import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { communityIdFromRouteParam } from "@/api/contracts/communities";
import { specListSearchSchema } from "@/api/contracts/specifications";
import { CommunityDetailsPage } from "@/components/communities/community-details-page";
import { communityDetailsQueryKey, type CommunityDetails } from "@/hooks/communities";
import { communityPageTitle } from "@/lib/community-page-title";
import { requireInternalAudience } from "@/lib/route-guards";
import { specListFiltersNavigation, specListSortNavigation } from "@/lib/spec-list-url-state";

// One community's details page (#751): every community link lands here — the
// list's ID and Name cells, a spec's Community ID, a breadcrumb — at the donor's
// `communities/:internalCommunityId/:fullCommunityId` (`App.tsx:113`).
//
// - **The first segment is the key; the second is never read**, and the URL is
//   never rewritten: a stale or mistyped full id still opens the community,
//   and the title shows the read's own (parity).
// - **Gated by audience only**, as the list is: the source checks sign-in only.
// - **No loader.** The page draws its skeletons while the read runs, as the
//   donor island does; a loader would hold the navigation behind the live
//   feed's 10s ceiling.
// - **`?sort=` and `?filters=` are the single-community spec table's**, raw,
//   in the Spec list's own schema; its writers are the Spec list's, aimed here.

/**
 * The tab's title from whatever the cache holds for this community — so the
 * router's head, re-projected on the table's `?sort=` / `?filters=` writes,
 * says what the page set, never the bare fallback (story 18). The page also
 * writes `document.title` itself on every change — nothing re-projects the
 * head when the read answers, and a head already reading the fallback writes
 * nothing over the last community's name.
 */
function cachedTitle(queryClient: QueryClient | undefined, param: string | undefined): string {
  const communityId = communityIdFromRouteParam(param);
  const cached =
    typeof communityId === "number"
      ? queryClient?.getQueryData<CommunityDetails>(communityDetailsQueryKey(communityId))
      : undefined;
  return communityPageTitle(cached ?? undefined);
}

export const Route = createFileRoute(
  "/_authed/_chrome/communities/$internalCommunityId/$fullCommunityId",
)({
  validateSearch: specListSearchSchema,
  beforeLoad: ({ context }) => {
    requireInternalAudience(context.currentUser.user);
  },
  head: ({ match, params }) => ({
    meta: [{ title: cachedTitle(match?.context?.queryClient, params?.internalCommunityId) }],
  }),
  component: CommunityDetailsRoute,
});

function CommunityDetailsRoute() {
  const { internalCommunityId } = Route.useParams();
  const { sort, filters } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <CommunityDetailsPage
      communityId={communityIdFromRouteParam(internalCommunityId)}
      rawSort={sort}
      rawFilters={filters}
      // Sort: replace. Filters: push, so Back undoes one — the Spec list's
      // rules, the routing test's to run.
      onSortChange={(next) => void navigate(specListSortNavigation(next))}
      onFiltersChange={(next) => void navigate(specListFiltersNavigation(next))}
    />
  );
}
